// ─────────────────────────────────────────────────────────────────────────────
//  MenuMaster  ·  Modern Soft-Gradient Food  ·  Warm + Red Accent Palette
// ─────────────────────────────────────────────────────────────────────────────

// ── Food-theme warm tones ─────────────────────────────────────────────────────
export const food = {
  // Page backgrounds
  bg:         '#FBF9F6',   // warm off-white
  bgPeach:    '#FFE6E1',   // corner gradient — soft peach
  bgMint:     '#E8F2EA',   // corner gradient — soft mint

  // Glass card surface
  glass:      'rgba(255,255,255,0.82)',
  glassBorder:'rgba(255,255,255,0.9)',
  glassDark:  'rgba(28,24,22,0.72)',
  glassDarkBorder:'rgba(255,255,255,0.08)',

  // Dark mode backgrounds
  darkBg:     '#1C1816',
  darkSurface:'#231F1C',
  darkSidebar:'#18150E',

  // Accent — vibrant food red
  red:        '#E02A2A',
  redLight:   '#FF5A5A',
  redDark:    '#B71C1C',
  redGlow:    'rgba(224,42,42,0.28)',
  redSoft:    'rgba(224,42,42,0.10)',

  // Neutral
  inkDark:    '#1A1209',
  inkMid:     '#4A3728',
  inkLight:   '#8A7060',
  inkMuted:   '#B8A898',

  // Dark text
  snowPrimary:  '#F5EFE8',
  snowSecondary:'#C0A88A',
  snowMuted:    '#7A6658',
};

// Glass card shadow
const glassShadow = '0 8px 32px rgba(26,18,9,0.08), 0 2px 8px rgba(26,18,9,0.04)';
const glassShadowLg = '0 16px 48px rgba(26,18,9,0.12), 0 4px 16px rgba(26,18,9,0.06)';
const glassShadowDark = '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)';
const glassShadowDarkLg = '0 16px 48px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35)';

// ── neu kept for backward compat (re-exported as glass-style) ─────────────────
export const neu = {
  light: {
    bg:            food.bg,
    surface:       food.glass,
    sidebar:       '#F5F0EA',
    border:        'rgba(200,185,170,0.3)',
    textPrimary:   food.inkDark,
    textSecondary: food.inkMid,
    textMuted:     food.inkLight,
    shadowLight:   'rgba(255,255,255,0.9)',
    shadowDark:    'rgba(26,18,9,0.08)',
    raised:        glassShadow,
    raisedSm:      '0 4px 16px rgba(26,18,9,0.06), 0 1px 4px rgba(26,18,9,0.04)',
    raisedLg:      glassShadowLg,
    inset:         'inset 0 2px 8px rgba(26,18,9,0.08)',
    insetSm:       'inset 0 1px 4px rgba(26,18,9,0.06)',
    flat:          '0 1px 3px rgba(26,18,9,0.06)',
  },
  dark: {
    bg:            food.darkBg,
    surface:       food.darkSurface,
    sidebar:       food.darkSidebar,
    border:        'rgba(255,255,255,0.07)',
    textPrimary:   food.snowPrimary,
    textSecondary: food.snowSecondary,
    textMuted:     food.snowMuted,
    shadowLight:   'rgba(255,255,255,0.04)',
    shadowDark:    'rgba(0,0,0,0.6)',
    raised:        glassShadowDark,
    raisedSm:      '0 4px 16px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.25)',
    raisedLg:      glassShadowDarkLg,
    inset:         'inset 0 2px 8px rgba(0,0,0,0.4)',
    insetSm:       'inset 0 1px 4px rgba(0,0,0,0.3)',
    flat:          '0 1px 3px rgba(0,0,0,0.35)',
  },
};

// ── Brand Colors (kept for legacy imports) ────────────────────────────────────
export const brand = {
  blue: {
    50:    '#FFE6E1',
    100:   '#FFCDC7',
    300:   '#FF7A7A',
    500:   food.red,
    600:   food.redDark,
    700:   '#8B1111',
    main:  food.red,
    light: food.redLight,
    dark:  food.redDark,
    glow:  food.redGlow,
  },
  green: {
    50:    '#E8F2EA',
    100:   '#C8E6CC',
    300:   '#81C784',
    500:   '#388E3C',
    600:   '#2E7D32',
    main:  '#388E3C',
    light: '#66BB6A',
    dark:  '#2E7D32',
    glow:  'rgba(56,142,60,0.28)',
  },
  teal: {
    main:  '#E02A2A',
    light: '#FF5A5A',
    glow:  food.redGlow,
  },
};

// ── Semantic ──────────────────────────────────────────────────────────────────
export const colors = {
  primary: { main: food.red, light: food.redLight, dark: food.redDark },
  accent: {
    indigo:   food.red,
    violet:   '#D4845A',
    fuchsia:  '#E07B2A',
    pink:     '#F5A623',
    gradient: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
    gradientHorizontal: `linear-gradient(90deg, ${food.red} 0%, ${food.redLight} 100%)`,
    gradientSubtle: `linear-gradient(135deg, ${food.redSoft} 0%, rgba(224,42,42,0.05) 100%)`,
  },
  dark: {
    background:       food.darkBg,
    surface:          food.darkSurface,
    surfaceHighlight: '#2A2420',
    surfaceHover:     '#221E1A',
    border:           'rgba(255,255,255,0.07)',
    borderLight:      'rgba(255,255,255,0.04)',
    textPrimary:      food.snowPrimary,
    textSecondary:    food.snowSecondary,
    textMuted:        food.snowMuted,
  },
  light: {
    background:       food.bg,
    surface:          food.glass,
    surfaceHighlight: '#FFF6F2',
    surfaceHover:     '#F5EDE6',
    border:           'rgba(200,185,170,0.3)',
    borderLight:      'rgba(200,185,170,0.15)',
    textPrimary:      food.inkDark,
    textSecondary:    food.inkMid,
    textMuted:        food.inkLight,
  },
  success: { main: '#388E3C', light: '#66BB6A', dark: '#2E7D32', glow: 'rgba(56,142,60,0.28)' },
  warning: { main: '#F57C00', light: '#FF9800', dark: '#E65100', glow: 'rgba(245,124,0,0.28)' },
  error:   { main: food.red,  light: food.redLight, dark: food.redDark, glow: food.redGlow },
  info:    { main: '#1565C0', light: '#3D8FE0', dark: '#0D47A1', glow: 'rgba(21,101,192,0.28)' },
  glass: {
    background:      food.glass,
    backgroundLight: 'rgba(255,255,255,0.92)',
    backgroundDark:  food.glassDark,
    border:          'rgba(255,255,255,0.6)',
    borderLight:     'rgba(255,255,255,0.85)',
    blur:            'blur(24px)',
  },
};

// ── MUI palette tokens ────────────────────────────────────────────────────────
export const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    primary: {
      main:         food.red,
      light:        food.redLight,
      dark:         food.redDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main:         '#388E3C',
      light:        '#66BB6A',
      dark:         '#2E7D32',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'dark' ? food.darkBg      : food.bg,
      paper:   mode === 'dark' ? food.darkSurface  : food.glass,
    },
    text: {
      primary:   mode === 'dark' ? food.snowPrimary   : food.inkDark,
      secondary: mode === 'dark' ? food.snowSecondary : food.inkMid,
    },
    divider:   mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(200,185,170,0.25)',
    success: { main: '#388E3C',  light: '#66BB6A',   dark: '#2E7D32'   },
    warning: { main: '#F57C00',  light: '#FF9800',   dark: '#E65100'   },
    error:   { main: food.red,   light: food.redLight, dark: food.redDark },
    info:    { main: '#1565C0',  light: '#3D8FE0',   dark: '#0D47A1'   },
  },
});

// ── Gradients ─────────────────────────────────────────────────────────────────
export const gradients = {
  mesh: {
    light: `radial-gradient(ellipse 60% 50% at 0% 0%, ${food.bgPeach} 0%, transparent 70%),
            radial-gradient(ellipse 60% 50% at 100% 100%, ${food.bgMint} 0%, transparent 70%)`,
    dark:  `radial-gradient(ellipse 60% 50% at 0% 0%, rgba(224,42,42,0.08) 0%, transparent 70%),
            radial-gradient(ellipse 60% 50% at 100% 100%, rgba(56,142,60,0.06) 0%, transparent 70%)`,
  },
  aurora: {
    light: `linear-gradient(135deg, ${food.bgPeach} 0%, ${food.bgMint} 100%)`,
    dark:  `linear-gradient(135deg, rgba(224,42,42,0.15) 0%, rgba(56,142,60,0.1) 100%)`,
  },
  glow: {
    primary:   `radial-gradient(circle, ${food.redGlow} 0%, transparent 70%)`,
    secondary: `radial-gradient(circle, rgba(56,142,60,0.25) 0%, transparent 70%)`,
  },
  brand: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
};

// ── Shadows ───────────────────────────────────────────────────────────────────
export const shadows = {
  sm:  '0 1px 3px rgba(26,18,9,0.06)',
  md:  '0 4px 12px rgba(26,18,9,0.08)',
  lg:  glassShadow,
  xl:  glassShadowLg,
  glow: {
    primary:   `0 0 24px ${food.redGlow}`,
    primaryLg: `0 0 40px ${food.redGlow}`,
    secondary: '0 0 24px rgba(56,142,60,0.25)',
    accent:    `0 0 24px ${food.redGlow}`,
  },
  glass:     glassShadow,
  glassDark: glassShadowDark,
  neu: neu,
};

// ── Border Radius ─────────────────────────────────────────────────────────────
export const borderRadius = {
  sm:    8,
  md:    14,
  lg:    20,
  xl:    32,
  '2xl': 40,
  full:  9999,
};
